import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { ISearchParser } from './search-parser.token';
import { SearchFilterField, SearchFilterSpec, SearchRouteKey } from '../models/search-filter-spec.model';
import { SearchVocabularyStore, VocabularyEntry } from '../state/search-vocabulary.store';
import { normalizeSearchText, tokenizeSearchQuery, tokenizeSearchQueryPreservingCase } from '../utils/search-normalize.util';
import { extractFieldLabel, FieldLabelMatch } from '../utils/search-field-label.util';
import { isStopWord, matchFieldValue, matchReservedKeyword, matchToken, MATCH_CONFIDENCE, TokenCandidate } from './search-token-matcher';

/** Fields that accumulate MULTIPLE tokens' matches into an array; every other field is scalar
    (last-token-wins on a genuine collision, an edge case Phase 1 does not special-case). */
const ARRAY_FIELDS = new Set<SearchFilterField>(['entities', 'status', 'licenseType', 'department', 'user']);

interface Accumulator {
  filters: Partial<Record<SearchFilterField, unknown>>;
  matchedTerms: SearchFilterSpec['matchedTerms'];
  confidences: number[];
}

function applyCandidate(acc: Accumulator, candidate: TokenCandidate): void {
  const { field, value } = candidate.term;
  if (ARRAY_FIELDS.has(field)) {
    const existing = (acc.filters[field] as unknown[] | undefined) ?? [];
    acc.filters[field] = [...existing, value];
  } else {
    acc.filters[field] = value;
  }
  acc.matchedTerms.push(candidate.term);
  acc.confidences.push(candidate.matchConfidence);
}

function newAccumulator(): Accumulator {
  return { filters: {}, matchedTerms: [], confidences: [] };
}

function toSpec(acc: Accumulator, route: SearchRouteKey, unmatchedTokens: string[]): SearchFilterSpec {
  return {
    route,
    filters: acc.filters as SearchFilterSpec['filters'],
    matchedTerms: acc.matchedTerms,
    unmatchedTokens,
    confidence: acc.confidences.length ? Math.min(...acc.confidences) : 0,
  };
}

@Injectable()
export class DeterministicSearchParserService implements ISearchParser {
  private readonly vocabulary = inject(SearchVocabularyStore);
  private readonly translate = inject(TranslateService);

  parse(query: string): Observable<SearchFilterSpec[]> {
    // A recognized leading field label ("license no -", "department:", "performer" …) hints
    // WHICH field the rest of the query targets — it is never itself part of the value (see
    // extractFieldLabel's doc). Handled entirely separately from the generic multi-field path
    // below: a labeled search's remainder is either a raw licenseNo value (never tokenized —
    // see search-field-label.util's class doc) or matched as ONE whole-phrase value scoped to
    // exactly that field, never fanned out token-by-token.
    const labeled = extractFieldLabel(query);
    if (labeled) return of(this.parseLabeled(labeled));

    const rawTokens = tokenizeSearchQuery(query);
    if (rawTokens.length === 0) return of([]);
    const casedTokens = tokenizeSearchQueryPreservingCase(query);

    // Route-keyword / license-term pre-pass: consume reserved tokens before vocabulary matching.
    // Route is a top-level spec field, not a filter, so a route-keyword match has no MatchedTerm
    // of its own — it just changes which page the resulting spec(s) target.
    let route: SearchRouteKey = 'workspace';
    let routeMatched = false;
    let licenseTerm: 'perpetual' | 'fixed' | undefined;
    const remainingTokens: string[] = [];
    const remainingCasedTokens: string[] = [];
    for (let i = 0; i < rawTokens.length; i++) {
      const token = rawTokens[i];
      const reserved = matchReservedKeyword(token);
      if (reserved?.route) {
        route = reserved.route;
        routeMatched = true;
        continue;
      }
      if (reserved?.licenseTerm) {
        licenseTerm = reserved.licenseTerm;
        continue;
      }
      // Dropped as meaningless before it ever reaches vocabulary matching — not "unrecognized
      // content", so it must never land in unmatchedTokens or spawn a licenseNo guess.
      if (isStopWord(token)) continue;
      remainingTokens.push(token);
      remainingCasedTokens.push(casedTokens[i]);
    }
    const vocabulary = this.vocabulary.entries();
    const perToken = remainingTokens.map((token, i) => ({
      token,
      cased: remainingCasedTokens[i],
      candidates: matchToken(token, vocabulary),
    }));

    const unambiguous = perToken.filter((t) => t.candidates.length === 1);
    const ambiguous = perToken.filter((t) => t.candidates.length > 1);
    const unmatched = perToken.filter((t) => t.candidates.length === 0);
    const unmatchedTokens = unmatched.map((t) => t.token);

    const base = newAccumulator();
    if (licenseTerm) {
      base.filters.licenseTerm = licenseTerm;
      const label = this.translate.instant(
        licenseTerm === 'perpetual' ? 'header.intentSearchValidityTypePerpetual' : 'header.intentSearchValidityTypeFixedTerm',
      );
      base.matchedTerms.push({ token: licenseTerm, field: 'licenseTerm', value: licenseTerm, label });
      base.confidences.push(MATCH_CONFIDENCE.keyword);
    }
    for (const t of unambiguous) applyCandidate(base, t.candidates[0]);

    const specs: SearchFilterSpec[] = [];
    if (ambiguous.length === 0) {
      if (base.matchedTerms.length > 0 || base.filters.licenseTerm || (routeMatched && unmatchedTokens.length === 0)) {
        specs.push(toSpec(base, route, unmatchedTokens));
      }
    } else {
      const [first, ...rest] = ambiguous;
      const restPicks = rest.map((t) => t.candidates[0]);
      for (const candidate of first.candidates) {
        const acc = newAccumulator();
        acc.filters = { ...base.filters };
        acc.matchedTerms = [...base.matchedTerms];
        acc.confidences = [...base.confidences];
        applyCandidate(acc, candidate);
        for (const pick of restPicks) applyCandidate(acc, pick);
        specs.push(toSpec(acc, route, unmatchedTokens));
      }
    }

    // Each unmatched token spawns its own licenseNo-candidate interpretation, using the
    // CASE-PRESERVED form of the token (recovered via tokenizeSearchQueryPreservingCase) as the
    // filter value/chip label — e.g. a bare 'LN_432156' must search/display 'LN_432156', not
    // the lowercased 'ln_432156' matchToken compared against the vocabulary.
    for (const t of unmatched) {
      const acc = newAccumulator();
      const value = t.cased;
      const hasDigit = /\d/.test(value);
      acc.filters.licenseNo = value;
      acc.matchedTerms.push({ token: t.token, field: 'licenseNo', value, label: this.translate.instant('header.intentSearchLicenseNumber', { value }) });
      acc.confidences.push(hasDigit ? MATCH_CONFIDENCE.licenseNoWithDigit : MATCH_CONFIDENCE.licenseNoNoDigit);
      specs.push(toSpec(acc, route, unmatchedTokens));
    }

    return of(specs);
  }

  /** Handles a query that started with a recognized field-label prefix (see
      `extractFieldLabel`). Always resolves to AT MOST one interpretation — a labeled search is
      an explicit, unambiguous statement of intent, not a fan-out candidate — and to NONE when
      the remainder is empty, meaningless (a lone stop word), or matches nothing in the labeled
      field: never fall back to guessing at a different field, and never surface a spec built
      from nothing usable (the caller's dropdown renders "nothing usable found" when this
      returns []). */
  private parseLabeled(labeled: FieldLabelMatch): SearchFilterSpec[] {
    if (labeled.target.kind === 'licenseNo') {
      return this.licenseNoSpec(labeled.rawValue);
    }
    return this.vocabularyFieldSpec(labeled.rawValue, labeled.target.field, labeled.target.role);
  }

  private licenseNoSpec(rawValue: string): SearchFilterSpec[] {
    const value = rawValue.trim();
    if (!value || !/[a-z0-9]/i.test(value) || isStopWord(value.toLowerCase())) return [];

    const acc = newAccumulator();
    const hasDigit = /\d/.test(value);
    acc.filters.licenseNo = value;
    acc.matchedTerms.push({ token: value, field: 'licenseNo', value, label: this.translate.instant('header.intentSearchLicenseNumber', { value }) });
    acc.confidences.push(hasDigit ? MATCH_CONFIDENCE.licenseNoWithDigit : MATCH_CONFIDENCE.licenseNoNoDigit);
    return [toSpec(acc, 'workspace', [])];
  }

  private vocabularyFieldSpec(rawValue: string, field: SearchFilterField, role?: 'P' | 'R'): SearchFilterSpec[] {
    const normalizedValue = normalizeSearchText(rawValue);
    if (!normalizedValue) return [];

    const scoped: VocabularyEntry[] = this.vocabulary
      .entries()
      .filter((e) => e.field === field && (!role || e.role === role));
    const candidate = matchFieldValue(normalizedValue, scoped);
    if (!candidate) return [];

    const acc = newAccumulator();
    applyCandidate(acc, candidate);
    return [toSpec(acc, 'workspace', [])];
  }
}
